import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import Message, {MessageStatus, MessengerState} from '@/types/message'
import Chat from '@/types/chat'
import {PATHS} from "@/utils/paths";

const initialState: MessengerState & {
    extraMessages: Record<string, Message[]>,
    chatMeta: any | null,
    chatsList: Chat[] | null,
    isAtBottom: boolean,
    hasNewMessageWhileNotAtBottom: boolean,
    initialScrollPending: boolean,

} = {
    selectedChat: null,
    pagesToBeLoaded: [],
    repliedOn: null,
    chatIsLoading: true,
    lastUnseenMessageOnScreen: null,
    dockedSupportOpen: false,
    extraMessages: {},
    chatsList: null,
    chatMeta: null,
    hasNewMessageWhileNotAtBottom: false,
    isAtBottom: true,
    initialScrollPending: false,
}

export const messengerSlice = createSlice({
    name: 'messenger',
    initialState,
    reducers: {
        setSelectedChat: (state, action) => {
            if (state.selectedChat?.id === action.payload?.id) return
            state.selectedChat = action.payload
            state.initialScrollPending = Boolean(action.payload)
            state.chatMeta = null
            const newUrl = `${PATHS.USER.MESSENGER.CHAT(String(action.payload?.id ?? ''))}`
            window.history.replaceState({...window.history.state, as: newUrl, url: newUrl}, '', newUrl)
            setRepliedOn(null)
            state.chatIsLoading = true
            if (action.payload) {
                const p = action.payload.firstUnreadMessagePage ?? 1
                state.pagesToBeLoaded = [p]

            }
        },
        setSelectedChatRaw: (state, action: PayloadAction<null | Chat>) => {
            state.selectedChat = action.payload
            state.initialScrollPending = Boolean(action.payload)
            state.chatMeta = null
            setRepliedOn(null)
            if (action.payload)
                state.pagesToBeLoaded = [action.payload.firstUnreadMessagePage ?? 1];
        },
        addToPagesToBeLoaded: (state, action: PayloadAction<number>) => {
            if (!state.pagesToBeLoaded.includes(action.payload) && action.payload > 0) {
                state.pagesToBeLoaded.push(action.payload)
                state.pagesToBeLoaded.sort((a, b) => a - b)
            }
        },
        setRepliedOn: (state, action: PayloadAction<null | Message>) => {
            state.repliedOn = action.payload
        },
        setChatIsLoading: (state, action: PayloadAction<boolean>) => {
            state.chatIsLoading = action.payload
        },
        updateLastOnSeenMessageOnScreen: (state, action: PayloadAction<{ messageId: number; chatId: string }>) => {
            const isNewer =
                state.lastUnseenMessageOnScreen?.chatId === action.payload.chatId &&
                state.lastUnseenMessageOnScreen?.messageId < action.payload.messageId
            const isNewChat = state.lastUnseenMessageOnScreen?.chatId !== action.payload.chatId
            const isSameMessage =
                state.lastUnseenMessageOnScreen?.chatId === action.payload.chatId &&
                state.lastUnseenMessageOnScreen?.messageId === action.payload.messageId
            if (isNewChat || isNewer || isSameMessage) state.lastUnseenMessageOnScreen = action.payload
        },
        setDockedSupportOpen: (state, action: PayloadAction<boolean>) => {
            state.dockedSupportOpen = action.payload
        },
        addIncomingMessage: (state, action: PayloadAction<Message & { chatId: string }>) => {
            const {chatId, ...msg} = action.payload
            if (!state.extraMessages[chatId]) state.extraMessages[chatId] = []
            const exists = state.extraMessages[chatId].some(m => m.id === msg.id)
            if (!exists) state.extraMessages[chatId].push(msg)
        },
        registerIncomingUnread: (state, action: PayloadAction<{chatId: string; messageId: number}>) => {
            const selectedChatId = String(state.selectedChat?.id ?? '').trim().toUpperCase();
            const incomingChatId = String(action.payload.chatId).trim().toUpperCase();
            if (!selectedChatId || selectedChatId !== incomingChatId) return;
            if (!Number.isFinite(action.payload.messageId) || action.payload.messageId <= 0) return;

            const applyUnread = (target: {unreadCount?: number; firstUnreadMessageId?: number} | null | undefined) => {
                if (!target) return;

                if ((target.unreadCount ?? 0) === 0 || target.firstUnreadMessageId === undefined) {
                    target.firstUnreadMessageId = action.payload.messageId;
                }
                target.unreadCount = (target.unreadCount ?? 0) + 1;
            };

            applyUnread(state.selectedChat);
            applyUnread(state.chatMeta);
        },
        updateMessagesSeen: (state, action: PayloadAction<(string | number)[]>) => {
            const ids = action.payload.map(v => Number(v))
            if (!ids?.length) return

            const maxSeenId = Math.max(...ids)

            if (state.selectedChat?.messages?.length) {
                state.selectedChat.messages = state.selectedChat.messages.map(m =>
                    Number(m.id) <= maxSeenId
                        ? {...m, status: MessageStatus.SEEN}
                        : m
                )
            }

            Object.keys(state.extraMessages).forEach(cid => {
                const msgs = state.extraMessages[cid]
                if (!msgs?.length) return

                state.extraMessages[cid] = msgs.map(m =>
                    Number(m.id) <= maxSeenId
                        ? {...m, status: MessageStatus.SEEN}
                        : m
                )
            })
        },
        setChatsList: (state, action: PayloadAction<Chat[] | null>) => {
            state.chatsList = action.payload
        },
        setChatMeta: (state, action) => {
            state.chatMeta = action.payload;

            if (state.selectedChat && action.payload) {
                state.selectedChat.firstUnreadMessageId =
                    action.payload.firstUnreadMessageId;
            }
        },
        removeMessages: (state, action: PayloadAction<{ chatId: string; ids: (string | number)[] }>) => {
            const {chatId, ids} = action.payload;
            if (!state.extraMessages[chatId]) return;
            state.extraMessages[chatId] = state.extraMessages[chatId].filter(
                (m) => !ids.map(String).includes(String(m.id))
            );
        },
        updateUnreadCount: (
            state,
            action: PayloadAction<
                | { chatId: string; unreadCount: number }
                | { chatId: string; messageId: number }
            >
        ) => {
            const chat = state.chatsList?.find(
                c => String(c.id) === String(action.payload.chatId)
            );

            if ('unreadCount' in action.payload) {
                if (chat) chat.unreadCount = action.payload.unreadCount;
                if (String(state.selectedChat?.id) === String(action.payload.chatId)) {
                    state.selectedChat!.unreadCount = action.payload.unreadCount;
                }
                if (state.chatMeta) state.chatMeta.unreadCount = action.payload.unreadCount;
                return;
            }

            const {messageId} = action.payload;
            const applyRead = (target: {unreadCount?: number; firstUnreadMessageId?: number} | null | undefined) => {
                if (target?.firstUnreadMessageId === undefined) return;
                if (messageId < target.firstUnreadMessageId) return;

                const seenCount = messageId - target.firstUnreadMessageId + 1;
                target.unreadCount = Math.max(0, (target.unreadCount ?? 0) - seenCount);

                if (target.unreadCount === 0) target.firstUnreadMessageId = undefined;
            };

            if (String(state.selectedChat?.id) === String(action.payload.chatId)) {
                applyRead(state.selectedChat);
                applyRead(state.chatMeta);
            }
        },
        synchronizeReadState: (state, action: PayloadAction<{
            chatId: string;
            unreadCount: number;
            firstUnreadMessageId?: number;
        }>) => {
            const targetChatId = String(action.payload.chatId).trim().toUpperCase();
            const applyState = (target: {unreadCount?: number; firstUnreadMessageId?: number} | null | undefined) => {
                if (!target) return;
                target.unreadCount = action.payload.unreadCount;
                target.firstUnreadMessageId = action.payload.firstUnreadMessageId;
            };

            const chat = state.chatsList?.find(
                item => String(item.id).trim().toUpperCase() === targetChatId
            );
            applyState(chat);

            if (String(state.selectedChat?.id ?? '').trim().toUpperCase() === targetChatId) {
                applyState(state.selectedChat);
                applyState(state.chatMeta);
            }
        },
        setIsAtBottom: (state, action: PayloadAction<boolean>) => {
            state.isAtBottom = action.payload
        },
        setInitialScrollPending: (state, action: PayloadAction<boolean>) => {
            state.initialScrollPending = action.payload
        },
        clearFirstUnread: (state, action: PayloadAction<{ chatId: string }>) => {
            const chatId = String(action.payload.chatId);

            if (state.selectedChat && String(state.selectedChat.id) === chatId) {
                state.selectedChat.firstUnreadMessageId = undefined;
                state.selectedChat.unreadCount = 0;
            }

            if (state.chatMeta) {
                state.chatMeta.firstUnreadMessageId = undefined;
            }
        },


    }
})

export const {
    setSelectedChatRaw,
    setSelectedChat,
    addToPagesToBeLoaded,
    setRepliedOn,
    setChatIsLoading,
    updateLastOnSeenMessageOnScreen,
    setDockedSupportOpen,
    addIncomingMessage,
    registerIncomingUnread,
    updateMessagesSeen,
    setChatsList,
    setChatMeta,
    removeMessages,
    updateUnreadCount,
    synchronizeReadState,
    setIsAtBottom,
    setInitialScrollPending,
    clearFirstUnread,
} = messengerSlice.actions

export default messengerSlice.reducer
